import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Building2, Mail, RefreshCw, Loader2 } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';

const STATUS_LABELS = {
  new: 'New',
  in_progress: 'In progress',
  responded: 'Responded',
  closed: 'Closed',
};
const STATUS_COLORS = {
  new: 'bg-blue-500/10 text-blue-700 border-blue-500/30',
  in_progress: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  responded: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  closed: 'bg-gray-500/10 text-gray-700 border-gray-500/30',
};

export const PartnerInquiriesCard = () => {
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetch = async () => {
    setLoading(true);
    const url = filter ? `/api/partners/inquiries?status=${filter}` : '/api/partners/inquiries';
    const { data, error } = await apiFetch(url);
    if (!error && data) setData(data);
    setLoading(false);
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (id, status) => {
    const { error } = await apiFetch(`/api/partners/inquiries/${id}/status`, {
      method: 'POST',
      body: { status },
    });
    if (error) {
      toast.error(typeof error === 'string' ? error : 'Update failed');
      return;
    }
    toast.success(`Marked ${STATUS_LABELS[status].toLowerCase()}.`);
    fetch();
  };

  return (
    <Card data-testid="partner-inquiries-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5 text-purple-600" /> Partner inquiries
          </CardTitle>
          <CardDescription>
            From the public <code className="text-xs">/for-organizations</code> page.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter || 'all'} onValueChange={(v) => setFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[150px]" data-testid="partner-filter-select">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ({data.counts.total ?? 0})</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v} ({data.counts[k] ?? 0})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetch} data-testid="partner-refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No inquiries yet.</p>
        ) : (
          <div className="space-y-3">
            {data.items.map((inq) => (
              <div
                key={inq.id}
                className="border rounded-lg p-4 hover:shadow-sm transition-shadow"
                data-testid={`partner-inquiry-${inq.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{inq.org_name}</span>
                      <Badge variant="outline" className={STATUS_COLORS[inq.status] || ''}>
                        {STATUS_LABELS[inq.status] || inq.status}
                      </Badge>
                      <Badge variant="outline" className="text-xs">Pkg {inq.package?.toUpperCase?.() || '?'}</Badge>
                      {inq.org_type && <span className="text-xs text-muted-foreground">{inq.org_type}</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {inq.contact_name}{inq.role ? ` · ${inq.role}` : ''} ·{' '}
                      <a href={`mailto:${inq.email}`} className="underline">{inq.email}</a>
                      {inq.audience_size && <> · {inq.audience_size}</>}
                    </p>
                    <p className="text-sm mt-2 whitespace-pre-wrap">{inq.message}</p>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      {new Date(inq.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button
                      asChild size="sm" variant="outline" className="h-7 text-xs"
                    >
                      <a href={`mailto:${inq.email}?subject=Re: Partnering with ClearCommunicationApp`}>
                        <Mail className="h-3.5 w-3.5 mr-1" /> Reply
                      </a>
                    </Button>
                    <Select value={inq.status} onValueChange={(v) => updateStatus(inq.id, v)}>
                      <SelectTrigger
                        className="h-7 text-xs w-[130px]"
                        data-testid={`partner-status-${inq.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PartnerInquiriesCard;
